/**
 * ShortLinksContent - Content for Short Links tab
 */

import React from 'react';
import { JatakaResponse } from '../../../../../models/api';
import { Text, VStack, HStack, Badge, Spinner } from '@chakra-ui/react';
import { ShareLinkButton } from '../../../../share/ShareLinkButton';
import { ShortLinkItem } from './ShortLinkItem';
import { buildShortLinkUrl } from '../../../../../utils/urlHelpers';

export interface ShortLinksContentProps {
    apiResponse: JatakaResponse;
    chartQuota: { active_count: number; max_allowed: number } | null;
    loadingQuota: boolean;
    backendShares: any[];
    onDelete: (code: string) => Promise<void>;
}

export const ShortLinksContent: React.FC<ShortLinksContentProps> = ({
    apiResponse,
    chartQuota,
    loadingQuota,
    backendShares,
    onDelete
}) => {
    const isQuotaFull = chartQuota ? chartQuota.active_count >= chartQuota.max_allowed : false;

    return (
        <VStack align="stretch" spacing={2} p={2}>
            {/* Create button with quota */}
            <HStack spacing={3} align="center">
                <ShareLinkButton
                    variant="button"
                    size="sm"
                    chartData={apiResponse}
                    isDisabled={isQuotaFull}
                />

                {/* Quota display */}
                {loadingQuota ? (
                    <Spinner size="sm" color="blue.500" />
                ) : chartQuota ? (
                    <HStack spacing={1}>
                        <Text fontSize="xs" color="gray.600">
                            Quota:
                        </Text>
                        <Badge
                            colorScheme={chartQuota.active_count >= chartQuota.max_allowed ? 'red' : chartQuota.active_count === chartQuota.max_allowed - 1 ? 'orange' : 'green'}
                            fontSize="xs"
                        >
                            {chartQuota.active_count} / {chartQuota.max_allowed}
                        </Badge>
                    </HStack>
                ) : null}
            </HStack>

            {/* Quota warning */}
            {isQuotaFull && (
                <Text fontSize="xs" color="orange.600" fontStyle="italic">
                    Quota full. Delete some links to create new ones.
                </Text>
            )}

            {/* Short links list */}
            {backendShares.length > 0 ? (
                <VStack align="stretch" spacing={1} mt={1}>
                    <Text fontSize="2xs" fontWeight="bold" color="gray.600" textTransform="uppercase" mb={1}>
                        Existing Links
                    </Text>
                    {backendShares.map((link: any) => (
                        <ShortLinkItem
                            key={link.short_code}
                            url={buildShortLinkUrl(link.short_code)}
                            code={link.short_code}
                            onDelete={onDelete}
                            shareConfig={link.share_config}
                        />
                    ))}
                </VStack>
            ) : (
                <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    No short links created yet
                </Text>
            )}
        </VStack>
    );
};
